import { ref, computed } from '@vue/composition-api'
import {
  apiToastSuccess,
  apiToastWarning,
} from '@/@core/utils/toast'
import {
  ACCEPT_ORDER_MODIFICATIONS,
  APPROVE_ORDER,
  CANCEL_ORDER_MODIFICATIONS,
  CANCEL_ORDER,
  REJECT_ORDER,
  UPDATE_ORDER,
  MODIFY_ORDER_QUANTITIES,
} from '@/store/modules/order.module'
import store from '@/store'
import analytics from '@/@core/utils/analytics'
import constants, {
  FLAG_CONSTANTS,
  MONTH_START_DATE,
} from '@/constants'
import { formatObject } from '@/@core/utils/utils'
import { hasBeenChanged } from '@/views/apps/order/utils/hasBeenChanged'

const { ORDER_STATUS } = constants

const SAVE = 'save'
const MODIFY = 'modify'
const MODIFY_AND_CONFIRM = 'modify_and_confirm'
// KP-4363: confirm the lines the brand selected, keep the rest open
const CONFIRM_SELECTED = 'confirm_selected'

const APPROVE_TEXT = 'Approve'
const ACCEPT_CHANGES_TEXT = 'Accept Changes'

export function isPartiallyConfirmedStatus(order) {
  return order?.status === ORDER_STATUS.PARTIALLY_CONFIRMED
}

// KP-4363: the brand sent changes on a partially confirmed order; the backend
// gives the retailer the accept action until they accept them.
export function isAcceptModificationsOrder(order) {
  return !!order?.isAcceptModificationsEnabled
}

// KP-4363: the button reads Approve on a partially confirmed order too; there
// it confirms the remaining products (the confirm modal says so). For the
// retailer with changes to accept it reads Accept Changes.
export function getApproveActionText(order) {
  return isAcceptModificationsOrder(order) ? ACCEPT_CHANGES_TEXT : APPROVE_TEXT
}

// PUT /products moves the order to pending_retailer, which would drop the
// partial confirmation, so the backend refuses it while partially confirmed.
export function isModifyProductsActionAllowed(order) {
  return !isPartiallyConfirmedStatus(order)
}

// Confirmed lines are locked; modify-quantities rejects any entry for one,
// so the Save payload carries only active, unconfirmed lines.
export function isProductIncludedInSave(product) {
  return !product.isDeletedByBrand
    && !product.isDeletedByRetailer
    && product.status !== constants.ORDER_PRODUCT_SHIPMENT_STATUS.CONFIRMED.LABEL
}

export function useOrderActions() {
  const loading = ref(false)
  const isModify = ref(false)
  const modalTitle = ref('')
  const modalBody = ref('')
  const action = ref('')
  const hasError = ref(false)
  const selectedAction = ref({ text: '', action: '' })
  const expectedDate = ref(null)
  const sendEmailToRetailer = ref(false)
  const approveActionText = ref(APPROVE_TEXT)
  const isPartiallyConfirmed = ref(false)

  const isBrand = computed(() => store.getters.isBrand)
  const isRetailer = computed(() => store.getters.isRetailer)

  const isModifyOrderImportExportEnabled = computed(
    () => store.getters['flags/hasFeature'](FLAG_CONSTANTS.KP_2682_MODIFY_ORDER),
  )

  // KP-4363: on a partially confirmed order the retailer cancels only while
  // the brand's changes wait for them, like a pending_retailer order.
  function isCancelEnabled(order) {
    const cancelIncludesStatus = [
      ORDER_STATUS.PENDING_RETAILER,
      ORDER_STATUS.NEW,
    ]
    if (!isRetailer.value) return false
    if (isPartiallyConfirmedStatus(order)) return !!order?.isPendingRetailerConfirmation
    return cancelIncludesStatus.includes(order?.status)
  }

  // KP-4363: while the brand's changes wait for the retailer on a partially
  // confirmed order, the retailer's Cancel drops only the modified products.
  function isCancelModificationsOrder(order) {
    return isRetailer.value && isPartiallyConfirmedStatus(order) && !!order?.isPendingRetailerConfirmation
  }

  function isRejectEnabled(order) {
    return isBrand.value && order?.isRejectEnabled
  }

  function isBrandActionableStatus(order) {
    return order?.status === ORDER_STATUS.ACCEPTED
      || order?.status === ORDER_STATUS.MODIFIED
      || order?.status === ORDER_STATUS.PARTIALLY_CONFIRMED
  }

  function isModifyButtonEnabled(order) {
    return isBrand.value && isBrandActionableStatus(order)
  }

  // KP-4363: the brand waits while the retailer has changes to accept
  function isAwaitingRetailer(order) {
    return isPartiallyConfirmedStatus(order) && !!order?.isPendingRetailerConfirmation
  }

  function isApproveEnabled(order) {
    if (isBrand.value && isBrandActionableStatus(order)) return !isAwaitingRetailer(order)
    if (isRetailer.value && order?.status === ORDER_STATUS.PENDING_RETAILER) return true
    if (isRetailer.value && isAcceptModificationsOrder(order)) return true
    return false
  }

  function isOrderActionEnabled(order) {
    return isApproveEnabled(order) || isCancelEnabled(order)
  }

  function getActionModalConfig(actionType) {
    const modalConfigs = {
      [ORDER_STATUS.CONFIRMED]: {
        title: 'Order Confirmation',
        body: 'Are you sure you want to approve the order?',
        action: ORDER_STATUS.CONFIRMED,
      },
      [ORDER_STATUS.CANCELLED]: {
        title: 'Order Cancel',
        body: 'Are you sure you want to cancel the order?',
        action: ORDER_STATUS.CANCELLED,
      },
      [ORDER_STATUS.REJECTED]: {
        title: 'Order Reject',
        body: 'Are you sure you want to reject the order?',
        action: ORDER_STATUS.REJECTED,
      },
      [SAVE]: {
        title: 'Save Order',
        body: 'Are you sure you want to save the modified product quantities?',
        action: SAVE,
      },
      [MODIFY]: {
        title: 'Modify Order',
        body: 'Are you sure you want to modify the product quantities?',
        action: MODIFY,
      },
      [MODIFY_AND_CONFIRM]: {
        title: 'Modify Order & Confirm',
        body: 'Are you sure you want to modify the product quantities and confirm the order?',
        action: MODIFY_AND_CONFIRM,
      },
      [CONFIRM_SELECTED]: {
        title: 'Confirm Selected Products',
        body: 'Are you sure you want to confirm the selected products? The other products stay open for changes.',
        action: CONFIRM_SELECTED,
      },
    }
    return modalConfigs[actionType]
  }

  function getAcceptModificationsModalConfig() {
    return {
      title: 'Accept Changes',
      body: 'Are you sure you want to accept the changes the brand made to this order? The order will be confirmed.',
      action: ORDER_STATUS.CONFIRMED,
    }
  }

  function getApproveModalConfig(order) {
    if (isAcceptModificationsOrder(order)) return getAcceptModificationsModalConfig()
    if (isPartiallyConfirmedStatus(order)) return getConfirmRemainingModalConfig()
    return getActionModalConfig(ORDER_STATUS.CONFIRMED)
  }

  function getConfirmRemainingModalConfig() {
    return {
      title: 'Confirm Remaining Products',
      body: 'Are you sure you want to confirm the remaining products? This confirms the whole order.',
      action: ORDER_STATUS.CONFIRMED,
    }
  }

  function getConfirmSelectedModalConfig(count) {
    const productsText = count === 1 ? '1 selected product' : `${count} selected products`
    return {
      ...getActionModalConfig(CONFIRM_SELECTED),
      body: `Are you sure you want to confirm ${productsText}? The other products stay open for changes.`,
    }
  }

  function syncPartialConfirmState(order) {
    approveActionText.value = getApproveActionText(order)
    isPartiallyConfirmed.value = isPartiallyConfirmedStatus(order)
  }

  function hasRelevantChangesToSave(order, originalOrder, importedProductStatuses, isInDraft) {
    const hasQuantityChanges = hasBeenChanged(order?.products, originalOrder?.products)
    const hasImportedStatusChanges = (importedProductStatuses?.length ?? 0) > 0
    return hasQuantityChanges || isInDraft || hasImportedStatusChanges
  }

  function isOkayToModify({
 order, originalOrder, importedProductStatuses, isInDraft,
}) {
    if (!isModify.value) {
      apiToastWarning('Please click on modify to change product quantities')
      return false
    }
    if (!hasRelevantChangesToSave(order, originalOrder, importedProductStatuses, isInDraft)) {
      apiToastWarning('Please modify the quantities before save/confirm')
      return false
    }
    return true
  }

  const MODIFY_ACTIONS = new Set([SAVE, MODIFY, MODIFY_AND_CONFIRM])

  function isModifyAction(actionType) {
    return MODIFY_ACTIONS.has(actionType)
  }

  function trackSaveOrderModification(order) {
    analytics.track(
      constants.TRACKS.ACTIONS.SINGLE_ORDER.SAVE_ORDER_MODIFICATIONS,
      formatObject({
        orderId: order.orderSeqId,
        collectionId: order.collectionId,
        collectionName: order.collectionName,
      }),
    )
  }

  function getModalConfigFor(actionType, order) {
    if (actionType === ORDER_STATUS.CONFIRMED) return getApproveModalConfig(order)
    if (actionType === ORDER_STATUS.CANCELLED && isCancelModificationsOrder(order)) {
      return {
        title: 'Cancel Changes',
        body: 'Are you sure you want to cancel the products the brand modified? The other products stay in the order.',
        action: ORDER_STATUS.CANCELLED,
      }
    }
    return getActionModalConfig(actionType)
  }

  function applyModalConfig(config) {
    modalTitle.value = config.title
    modalBody.value = config.body
    action.value = config.action
  }

  function openActionModal(actionType, {
 order, originalOrder, importedProductStatuses, isInDraft,
} = {}) {
    if (isModifyAction(actionType)
      && !isOkayToModify({
 order, originalOrder, importedProductStatuses, isInDraft,
})) {
      return null
    }

    if (actionType === SAVE) {
      trackSaveOrderModification(order)
    }

    const config = getModalConfigFor(actionType, order)
    if (!config) {
      return null
    }

    applyModalConfig(config)
    return config
  }

  function approveOrderData(orderId, loadOrder) {
    loading.value = true
    return store
      .dispatch(APPROVE_ORDER, orderId)
      .then(res => {
        loading.value = false
        apiToastSuccess(res.data.message || 'Approved this order!')
        loadOrder(orderId)
        return { success: true }
      })
      .catch(err => {
        loading.value = false
        return { success: false, err }
      })
  }

  function acceptOrderModifications(orderId, loadOrder) {
    loading.value = true
    return store
      .dispatch(ACCEPT_ORDER_MODIFICATIONS, orderId)
      .then(res => {
        loading.value = false
        apiToastSuccess(res.data?.message || 'Accepted the changes and confirmed the order!')
        loadOrder(orderId)
        return { success: true }
      })
      .catch(err => {
        loading.value = false
        return { success: false, err }
      })
  }

  function rejectOrder(orderId, loadOrder) {
    if (isRetailer.value) {
      apiToastWarning("You don't have permission!")
      return Promise.resolve({ success: false })
    }
    return store
      .dispatch(REJECT_ORDER, orderId)
      .then(res => {
        apiToastSuccess(res.data.message || 'Rejected this order!')
        loadOrder(orderId)
        return { success: true }
      })
      .catch(() => ({ success: false }))
  }

  function cancelOrder(orderId, loadOrder) {
    if (isBrand.value) {
      apiToastWarning("You don't have permission!")
      return Promise.resolve({ success: false })
    }
    return store
      .dispatch(CANCEL_ORDER, { orderId })
      .then(res => {
        apiToastSuccess(res.data.message || 'Cancelled this order!')
        loadOrder(orderId)
        return { success: true }
      })
      .catch(() => ({ success: false }))
  }

  function cancelOrderModifications(orderId, loadOrder) {
    return store
      .dispatch(CANCEL_ORDER_MODIFICATIONS, orderId)
      .then(res => {
        apiToastSuccess(res.data?.message || 'Cancelled the modified products!')
        loadOrder(orderId)
        return { success: true }
      })
      .catch(() => ({ success: false }))
  }

  // The retailer's Cancel: the modified products only while the brand's
  // changes wait for them on a partially confirmed order, else the order.
  function cancelOrderOrModifications(order, orderId, loadOrder) {
    if (isCancelModificationsOrder(order)) return cancelOrderModifications(orderId, loadOrder)
    return cancelOrder(orderId, loadOrder)
  }

  function getProductStatusChange(product, importedProductStatuses) {
    return importedProductStatuses?.find(s => s.productId === product.productId)
  }

  function findOriginalProduct(originalOrder, productId) {
    return originalOrder?.products?.find(p => p.productId === productId)
  }

  function findOriginalItem(originalProduct, sku) {
    return originalProduct?.items?.find(i => i.sku === sku || i.skuId === sku)
  }

  function getOriginalItemValue(originalOrder, productId, sku) {
    const originalProduct = findOriginalProduct(originalOrder, productId)
    const originalItem = findOriginalItem(originalProduct, sku)
    return originalItem?.value ?? originalItem?.quantity ?? 0
  }

  function shouldIncludeItemForSave(item, product, originalOrder) {
    if (item.value > 0) return true
    const originalValue = getOriginalItemValue(originalOrder, product.productId, item.sku)
    return item.value === 0 && originalValue > 0
  }

  function applyStatusChange(result, statusChange) {
    if (statusChange) {
      result.status = statusChange.status
    }
    return result
  }

  function mapCartonProduct(product, statusChange) {
    return applyStatusChange(
      { productId: product.productId, quantity: product.quantity },
      statusChange,
    )
  }

  function mapItemizedProduct(product, originalOrder, statusChange) {
    const items = product.items
      .filter(item => shouldIncludeItemForSave(item, product, originalOrder))
      .map(item => ({ skuId: item.sku, quantity: item.value }))
    return applyStatusChange(
      { productId: product.productId, items },
      statusChange,
    )
  }

  function mapProductForSave(product, originalOrder, importedProductStatuses) {
    const statusChange = getProductStatusChange(product, importedProductStatuses)
    if (product.isCarton) {
      return mapCartonProduct(product, statusChange)
    }
    return mapItemizedProduct(product, originalOrder, statusChange)
  }

  function hasCartonQuantityChange(product, originalOrder) {
    if (product.quantity > 0) return true
    const originalProduct = findOriginalProduct(originalOrder, product.productId)
    const originalQuantity = originalProduct?.quantity ?? 0
    return product.quantity === 0 && originalQuantity > 0
  }

  function shouldIncludeProductInSave(product, originalOrder) {
    if (product.status) return true
    if (product.quantity !== undefined) {
      return hasCartonQuantityChange(product, originalOrder)
    }
    return product.items && product.items.length > 0
  }

  function transformProductsForSave(order, originalOrder, importedProductStatuses) {
    return order.products
      .filter(isProductIncludedInSave)
      .map(product => mapProductForSave(product, originalOrder, importedProductStatuses))
      .filter(product => shouldIncludeProductInSave(product, originalOrder))
  }

  function getProductQuantity(product) {
    if (product.quantity !== undefined) return product.quantity
    return product.items.reduce((sum, item) => sum + item.quantity, 0)
  }

  function calculateTotalQuantity(products) {
    return products.reduce((sum, p) => sum + getProductQuantity(p), 0)
  }

  function buildSaveTrackingData(order, products) {
    return formatObject({
      orderId: order.orderSeqId,
      collectionId: order.collectionId,
      collectionName: order.collectionName,
      sendEmailToRetailer: sendEmailToRetailer.value,
      numberOfProducts: products.length,
      totalQuantity: calculateTotalQuantity(products),
    })
  }

  function resetAfterSave() {
    isModify.value = false
    selectedAction.value = {
      text: approveActionText.value,
      action: ORDER_STATUS.CONFIRMED,
    }
  }

  function saveOrder({
 orderId, order, originalOrder, importedProductStatuses, loadOrder,
}) {
    loading.value = true
    const products = transformProductsForSave(order, originalOrder, importedProductStatuses)
    const payload = {
      products,
      sendEmailToRetailer: sendEmailToRetailer.value,
    }
    const trackingData = buildSaveTrackingData(order, products)

    return store
      .dispatch(MODIFY_ORDER_QUANTITIES, { orderId, payload })
      .then(res => {
        loading.value = false
        apiToastSuccess(res.data.message || 'Saved order modifications!')
        sendEmailToRetailer.value = false
        analytics.track(
          constants.TRACKS.ACTIONS.SINGLE_ORDER.SAVE_ORDER_MODIFICATIONS_SUCCESS,
          trackingData,
        )
        return loadOrder(orderId).then(() => {
          resetAfterSave()
          return { success: true }
        })
      })
      .catch(err => {
        loading.value = false
        analytics.track(
          constants.TRACKS.ACTIONS.SINGLE_ORDER.SAVE_ORDER_MODIFICATIONS_FAILED,
          { ...trackingData, error: err },
        )
        return { success: false }
      })
  }

  function enableModify(enable = false) {
    isModify.value = enable || !isModify.value
    if (isModify.value && isPartiallyConfirmed.value) {
      selectedAction.value = {
        text: 'Save',
        action: SAVE,
      }
    } else if (isModify.value) {
      selectedAction.value = {
        text: 'Modify Order',
        action: MODIFY,
      }
    } else {
      selectedAction.value = {
        text: approveActionText.value,
        action: ORDER_STATUS.CONFIRMED,
      }
    }
  }

  function onClickSaveDate(order) {
    if (!expectedDate.value) {
      apiToastWarning('Please select the date')
      return Promise.resolve({ success: false })
    }
    const expectedAvailabilityDate = `${MONTH_START_DATE}${expectedDate.value}`
    loading.value = true
    const payload = { expectedAvailabilityDate }
    return store.dispatch(UPDATE_ORDER, { orderId: order._id, payload }).then(() => {
      order.expectedAvailabilityDate = expectedDate.value
      return {
        success: true,
        shouldApprove: action.value === ORDER_STATUS.CONFIRMED,
        shouldConfirmSelected: action.value === CONFIRM_SELECTED,
      }
    })
  }

  function initOrderActions(order, isModifyEnabled) {
    syncPartialConfirmState(order)
    if (isModifyEnabled) {
      enableModify(true)
    } else {
      const actionMap = [
        { condition: isApproveEnabled(order), text: getApproveActionText(order), action: ORDER_STATUS.CONFIRMED },
        { condition: isCancelEnabled(order), text: 'Cancel', action: ORDER_STATUS.CANCELLED },
        { condition: isRejectEnabled(order), text: 'Reject', action: ORDER_STATUS.REJECTED },
      ]
      const matchedAction = actionMap.find(item => item.condition)
      if (matchedAction) {
        selectedAction.value = {
          text: matchedAction.text,
          action: matchedAction.action,
        }
      }
    }
    if (order?.expectedAvailabilityDate) {
      expectedDate.value = order.expectedAvailabilityDate
    }
  }

  return {
    // State
    loading,
    isModify,
    modalTitle,
    modalBody,
    action,
    hasError,
    selectedAction,
    expectedDate,
    sendEmailToRetailer,
    approveActionText,
    isPartiallyConfirmed,

    // Computeds
    isBrand,
    isRetailer,
    isModifyOrderImportExportEnabled,

    // Methods
    isCancelEnabled,
    isRejectEnabled,
    isModifyButtonEnabled,
    isApproveEnabled,
    isOrderActionEnabled,
    getActionModalConfig,
    openActionModal,
    approveOrderData,
    acceptOrderModifications,
    rejectOrder,
    cancelOrder,
    cancelOrderModifications,
    cancelOrderOrModifications,
    isCancelModificationsOrder,
    saveOrder,
    enableModify,
    onClickSaveDate,
    initOrderActions,
    transformProductsForSave,
    hasRelevantChangesToSave,
    getConfirmSelectedModalConfig,
    syncPartialConfirmState,
    getApproveActionText,
    isModifyProductsActionAllowed,

    // Constants
    ORDER_STATUS,
    SAVE,
    MODIFY,
    MODIFY_AND_CONFIRM,
    CONFIRM_SELECTED,
  }
}
